import { RiSaveLine } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { TextLink } from "@itandi/itandi-bb-ui/TextLink";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import useLoader from "~/hooks/useLoader";
import type { DealParam } from "~/models/DealForm";
import { normalizeFields, shouldConfirmShowTenant } from "~/models/DealForm";
import { PATH } from "~/pages/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import { create } from "~/utils/api/Deal";
import { onInvalidScroll } from "~/utils/FormUtil";
import { leasingUrl } from "~/utils/NyukyoshaKanriUrlUtil";
import Styles from "./DealEditorFooter.module.scss";
import { ShowTenantConfirmModal } from "./ShowTenantConfirmModal";

export function DealEditorFooter() {
	const { notify } = useNotification();
	const router = useRouter();
	const { getValues, handleSubmit } = useFormContext<DealParam>();
	const { Loader, showLoader, hideLoader } = useLoader();
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const executeSave = useCallback(
		async (values: DealParam) => {
			showLoader();
			const response = await create(values);
			const responseId = response.data?.id;
			if (isSuccess(response) && responseId !== undefined) {
				await router.push(PATH(responseId));
				hideLoader();
				notify({ content: "作成しました。", variant: "success" });
				return;
			}
			hideLoader();
			notify({ content: "エラーが発生しました。", variant: "error" });
		},
		[showLoader, hideLoader, notify, router]
	);

	const onClickSubmit = useCallback(
		() =>
			handleSubmit(
				async (rawValues) => {
					const values = normalizeFields(rawValues);
					if (shouldConfirmShowTenant(values)) {
						setIsConfirmModalOpen(true);
						return;
					}
					await executeSave(values);
				},
				(errors) => {
					onInvalidScroll(errors);
				}
			)(),
		[handleSubmit, executeSave]
	);

	const handleConfirm = useCallback(async () => {
		setIsConfirmModalOpen(false);
		await executeSave(normalizeFields(getValues()));
	}, [executeSave, getValues]);

	const { leasing_id: leasingId } = router.query;
	return (
		<div className={Styles.DealEditorFooter}>
			<Loader />
			{typeof leasingId === "string" ? (
				<TextLink
					color="link"
					href={leasingUrl(leasingId)}
					size="s"
					target="_blank"
				>
					入居者管理に戻る
				</TextLink>
			) : null}
			<Button
				className={Styles.SaveButton}
				onClick={onClickSubmit}
				startIcon={<RiSaveLine className={Styles.Icon} />}
				variant="primary"
			>
				保存
			</Button>
			{isConfirmModalOpen ? (
				<ShowTenantConfirmModal
					onCancel={() => {
						setIsConfirmModalOpen(false);
					}}
					onConfirm={handleConfirm}
				/>
			) : null}
		</div>
	);
}
